import React, { useState } from 'react';
import { 
  Clock, 
  FileText, 
  DollarSign, 
  Layers, 
  FileEdit,
  ChevronRight,
  Calendar,
  Search,
  Filter
} from 'lucide-react';
import PastReportViewer from './PastReportViewer';

const PastReports = ({ 
  cbaHistory = [], 
  rewriteHistory = [], 
  bulkCBAHistory = [], 
  consolidationHistory = [],
  onSelectReport
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedReport, setSelectedReport] = useState(null);

  // Combine all histories with metadata
  const allReports = [
    ...cbaHistory.map((item, index) => ({
      ...item,
      type: 'cba',
      category: 'Cost-Benefit Analysis',
      icon: DollarSign,
      color: '#3b82f6',
      id: `cba-${index}`,
      title: item.regulationTitle || `CBA Report ${index + 1}`,
      regulation: item.regulationNumber || 'Unknown Regulation'
    })),
    ...rewriteHistory.map((item, index) => ({
      ...item,
      type: 'rewrite',
      category: 'Plain Language Rewrite',
      icon: FileEdit,
      color: '#10b981',
      id: `rewrite-${index}`,
      title: item.regulationTitle || `Rewrite ${index + 1}`,
      regulation: item.regulationNumber || 'Unknown Regulation'
    })),
    ...bulkCBAHistory.map((item, index) => ({
      ...item,
      type: 'bulk-cba',
      category: 'Bulk CBA',
      icon: DollarSign,
      color: '#6366f1',
      id: `bulk-${index}`,
      title: `Bulk Analysis - ${item.regulationCount || 'Multiple'} Regulations`,
      regulation: 'Multiple Regulations'
    })),
    ...consolidationHistory.map((item, index) => ({
      ...item,
      type: 'consolidation',
      category: 'Consolidation',
      icon: Layers,
      color: '#8b5cf6',
      id: `consolidation-${index}`,
      title: `Consolidated Regulation ${index + 1}`,
      regulation: 'Multiple Regulations'
    }))
  ];

  // Filter and sort reports
  const filteredReports = allReports
    .filter(report => {
      // Category filter
      if (selectedCategory !== 'all' && report.type !== selectedCategory) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          report.title.toLowerCase().includes(query) ||
          report.regulation.toLowerCase().includes(query) ||
          report.category.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } else {
        return new Date(a.timestamp) - new Date(b.timestamp);
      }
    });

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }
    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }
    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }
    
    // Format as date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const categories = [
    { id: 'all', label: 'All Reports', count: allReports.length },
    { id: 'cba', label: 'Cost-Benefit Analysis', count: cbaHistory.length },
    { id: 'rewrite', label: 'Plain Language', count: rewriteHistory.length },
    { id: 'bulk-cba', label: 'Bulk CBA', count: bulkCBAHistory.length },
    { id: 'consolidation', label: 'Consolidations', count: consolidationHistory.length }
  ];

  return (
    <>
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        background: 'linear-gradient(to bottom, #ffffff, #f9fafb)',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '16px',
          fontFamily: 'Playfair Display, serif'
        }}>
          Past Reports
        </h2>

        {/* Search and Filter Bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '18px',
              height: '18px',
              color: '#9ca3af'
            }} />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                background: 'white'
              }}
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '10px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>

        {/* Category Tabs */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '8px 16px',
                background: selectedCategory === cat.id 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'white',
                color: selectedCategory === cat.id ? 'white' : '#6b7280',
                border: selectedCategory === cat.id ? 'none' : '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              {cat.label}
              <span style={{
                background: selectedCategory === cat.id ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Reports List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
      }}>
        {filteredReports.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#9ca3af'
          }}>
            <Clock style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 16px',
              opacity: 0.3
            }} />
            <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
              No reports found
            </p>
            <p style={{ fontSize: '14px' }}>
              {searchQuery ? 'Try adjusting your search terms' : 'Generate some analyses to see them here'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredReports.map(report => {
              const Icon = report.icon;
              return (
                <div
                  key={report.id}
                  onClick={() => setSelectedReport(report)}
                  style={{
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: `linear-gradient(135deg, ${report.color}20, ${report.color}10)`,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon style={{ width: '24px', height: '24px', color: report.color }} />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#111827',
                      marginBottom: '4px'
                    }}>
                      {report.title}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      marginBottom: '8px'
                    }}>
                      {report.regulation}
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      fontSize: '12px',
                      color: '#9ca3af'
                    }}>
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Calendar style={{ width: '14px', height: '14px' }} />
                        {formatDate(report.timestamp)}
                      </span>
                      <span style={{
                        background: '#f3f4f6',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '500',
                        color: report.color
                      }}>
                        {report.category}
                      </span>
                      {report.context && (
                        <span style={{
                          background: '#fef3c7',
                          color: '#92400e',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          With Context
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight style={{
                    width: '20px',
                    height: '20px',
                    color: '#9ca3af'
                  }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>

    {/* Report Viewer Modal */}
    {selectedReport && (
      <PastReportViewer
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
      />
    )}
    </>
  );
};

export default PastReports;